import { useState, useRef, useEffect } from "react";
import { Search, Circle, PanelLeftOpen, HelpCircle, BookOpen, Keyboard, Bug } from "lucide-react";
import { NotificationCenter } from "./NotificationCenter";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { BugReportModal } from "./BugReportModal";
import { useSystemStatus } from "../../hooks/useSystemStatus";
import { cmdKey } from "../../utils/platform";

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  onSearchClick?: () => void;
  onHowItWorksClick?: () => void;
  onKeyboardShortcutsClick?: () => void;
  isDemoMode?: boolean;
  isSearchOpen?: boolean;
}

export function Header({ onMobileMenuToggle, onSearchClick, onHowItWorksClick, onKeyboardShortcutsClick, isDemoMode = false, isSearchOpen = false }: HeaderProps) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const { label: statusLabel, colors: statusColors } = useSystemStatus();

  // Track the trigger button so ESC can restore focus per WAI-ARIA menubutton.
  const helpTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setIsHelpOpen(false);
      }
    }
    // ESC dismissal — keyboard users opening the help menu (Tab + Enter)
    // previously had no dismissal path other than clicking outside or
    // tabbing through every item. WAI-ARIA menubutton spec requires ESC
    // to close and restore focus to the trigger.
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsHelpOpen(false);
        helpTriggerRef.current?.focus();
      }
    }
    if (isHelpOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isHelpOpen]);

  return (
    <>
      <header
        className="border-b border-border bg-background sticky top-0 z-40"
        style={{ height: 'var(--header-height)' }}
      >
        <div className={isDemoMode ? "h-full flex items-center justify-between px-4" : "h-full flex items-center justify-between px-2 sm:px-3 md:px-4"}>
          {/* Mobile Menu Button - HIDDEN in Demo Mode */}
          {!isDemoMode && (
            <button
              onClick={onMobileMenuToggle}
              className="lg:hidden p-1.5 sm:p-2 hover:bg-surface-1 border border-transparent hover:border-border transition-colors"
              aria-label="Toggle menu"
            >
              <PanelLeftOpen className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
            </button>
          )}

          {/* Mobile: Workspace - HIDDEN in Demo Mode */}
          {!isDemoMode && (
            <div
              className="lg:hidden absolute left-1/2 -translate-x-1/2 flex items-center"
              style={{ height: 'var(--header-height)' }}
            >
              <WorkspaceSwitcher inline large isDemoMode={isDemoMode} />
            </div>
          )}

          {/* Desktop: Workspace Switcher + Search */}
          <div className={isDemoMode ? "flex items-center flex-1 gap-2 max-w-2xl" : "hidden lg:flex items-center flex-1 gap-2"}>
            {!isDemoMode && <WorkspaceSwitcher inline />}

            {/* Search trigger — button, not fake input */}
            <button
              onClick={onSearchClick}
              className={`group flex-1 flex items-center gap-2.5 bg-surface-1 border px-3 py-2 transition-all duration-200 text-left ${isSearchOpen ? 'border-brand' : 'border-border hover:border-brand/40'}`}
            >
              <Search className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors duration-200 flex-shrink-0" />
              <span className="flex-1 text-xs sm:text-sm text-muted-foreground">
                Search traces, agents, policies...
              </span>
              <kbd className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-mono text-muted-foreground bg-surface-2 border transition-colors duration-200 flex-shrink-0 ${isSearchOpen ? 'border-brand' : 'border-border group-hover:border-brand/40'}`}>
                <span>{cmdKey}</span>
                <span>K</span>
              </kbd>
            </button>
          </div>

          {/* Right side */}
          <div className={isDemoMode ? "flex items-center gap-2 ml-4" : "flex items-center gap-1 sm:gap-2 lg:ml-4"}>
            {/* System Status — live from BetterStack */}
            <a
              href="https://status.adjudon.com"
              target="_blank"
              rel="noopener noreferrer"
              className={isDemoMode ? "flex items-center gap-1.5 px-2.5 py-2 bg-surface-1 border border-border hover:border-muted-foreground transition-colors" : "hidden lg:flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-2 bg-surface-1 border border-border hover:border-muted-foreground transition-colors"}
            >
              <Circle className={`w-1.5 h-1.5 ${statusColors.dot} adp-live`} />
              <span className={isDemoMode ? `text-[10px] text-mono ${statusColors.text} uppercase tracking-wider` : `text-[10px] sm:text-[10px] text-mono ${statusColors.text} uppercase tracking-wider`}>
                {statusLabel}
              </span>
            </a>

            {/* Help Menu */}
            <div className={isDemoMode ? "relative" : "hidden lg:block relative"} ref={helpRef}>
              <button
                ref={helpTriggerRef}
                onClick={() => setIsHelpOpen(!isHelpOpen)}
                className="relative p-2 hover:bg-surface-2 border border-transparent hover:border-border transition-colors group"
                aria-label="Help"
                aria-expanded={isHelpOpen}
              >
                <HelpCircle className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </button>

              {isHelpOpen && (
                <div className="absolute top-full right-0 mt-1 w-52 adp-dropdown z-50 animate-in slide-in-from-top-2 fade-in-0 duration-150">
                  <button
                    onClick={() => { setIsHelpOpen(false); onHowItWorksClick?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors text-left"
                  >
                    <BookOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-sm text-foreground">How it works</div>
                      <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mt-0.5">Platform overview</div>
                    </div>
                  </button>
                  <div className="border-t border-border" />
                  <button
                    onClick={() => { setIsHelpOpen(false); onKeyboardShortcutsClick?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors text-left"
                  >
                    <Keyboard className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div>
                      <div className="text-sm text-foreground">Keyboard shortcuts</div>
                      <div className="text-[10px] text-mono text-muted-foreground uppercase tracking-wider mt-0.5">{cmdKey}K · ?</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Bug Report — direct line to the founder. Visible on every
                breakpoint so mobile users can also surface issues; hidden
                only in demo mode where no authenticated user owns the
                report. */}
            {!isDemoMode && (
              <button
                onClick={() => setIsBugReportOpen(true)}
                className="relative p-1.5 sm:p-2 hover:bg-surface-2 border border-transparent hover:border-border transition-colors group"
                aria-label="Report a bug"
                title="Report a bug"
              >
                <Bug className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </button>
            )}

            {/* Theme toggle — icon-only, sits between Bug and Bell
                so the three header utilities share the same visual
                register (Bug · Theme · Bell). */}
            <ThemeToggle />

            {/* Notifications */}
            <NotificationCenter isDemoMode={isDemoMode} />
          </div>
        </div>
      </header>

      {/* Bug-report modal — controlled here so it survives any
          in-page click-outside on the help dropdown. */}
      <BugReportModal
        isOpen={isBugReportOpen}
        onClose={() => setIsBugReportOpen(false)}
      />

      {/* Mobile: Floating Bottom Search Bar - HIDDEN in Demo Mode */}
      {!isDemoMode && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[35] bg-background border-t border-border pt-2 pb-4 px-4 safe-area-bottom">
          <button
            onClick={onSearchClick}
            className="w-full flex items-center gap-2.5 bg-surface-1 border border-border px-3 py-2.5 hover:border-brand/40 transition-all duration-200 active:scale-[0.98]"
          >
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground flex-1 text-left">Search traces, agents, policies...</span>
            <kbd className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] text-mono text-muted-foreground bg-surface-2 border border-border">
              <span>{cmdKey}</span>
              <span>K</span>
            </kbd>
          </button>
        </div>
      )}
    </>
  );
}
